import React from 'react';
import { select } from '~/lib/select';
import { useNextSequence } from '~/training/useNextSequence';
import { useGlobalMediaStore } from '~/ui/video-player/GlobalMediaProvider';
import { Sequence, TrainingState, useTrainingStore } from '~/training/state';
import { usePrevSequence } from '~/training/usePrevSequence';
import {
  SequenceStore,
  useSequenceStore
} from '~/training/sequence/SequenceStore';
import { useQueryParams } from '~/lib/useQuery';
import LightButton from '~/editor/buttons/LightButton';
import styled from '@xstyled/styled-components';
import ArrowIcon from '~/assets/button/arrow.svg';
import DesktopIcon from '~/assets/button/desktop.svg';
import { t } from 'ttag';

const SkipSection = styled.div`
  width: 240px;
  height: 58px;
  background-color: #fff;
  border-radius: 16px;
  box-shadow: 3px 3px 5px #0000004f;
  left: 80px;
  top: 20px;
  position: absolute;
  z-index: hud;
  padding: 10px;
  display: flex;
  justify-content: space-between;

  button.arrow {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background-color: #f8f1ff;
    color: var(--color-accent-dk);
    padding: 0;

    svg {
      margin-left: auto;
      margin-right: auto;
      path {
        fill: var(--color-accent-dk) !important;
      }
    }
  }

  button.arrow:hover {
    svg {
      path {
        fill: #fff !important;
      }
    }
  }

  button.arrow-left {
    border-top-right-radius: 0px;
    border-bottom-right-radius: 0px;
  }

  button.arrow-right {
    border-left: 1px solid #e0e0e0;
    border-top-left-radius: 0px;
    border-bottom-left-radius: 0px;

    svg {
      transform: rotate(180deg);
    }
  }

  button.arrow:hover {
    background-color: var(--color-accent-dk) !important;
  }

  .devices {
    button {
      width: 38px;
      height: 38px;
      border-radius: 10px;
      background-color: var(--color-accent-dk) !important;
      padding: 0;
      svg {
        margin-left: auto;
        margin-right: auto;
      }
    }
  }
  .navBtns {
    margin-left: 10px;
    button {
      &:disabled {
        background-color: #e0e0e0 !important;
        svg {
          path {
            fill: grey !important;
          }
        }
      }
    }
  }
  .preview-mode {
    color: #000;
    font-family: Raleway;
    font-size: 12px;
    font-weight: 800;
    line-height: 18px;
    text-align: left;
    flex: 1;
    padding-left: 10px;
  }
`;

const selector = (s: TrainingState) =>
  select(s, 'skip', 'items', 'activeItemPosition');
const sequenceSelector = (s: SequenceStore) => select(s, 'clearBlocker');

export const SkipButtons = () => {
  const { skip, items, activeItemPosition } = useTrainingStore(selector);
  const { clearBlocker } = useSequenceStore(sequenceSelector);
  const params = useQueryParams();

  const next = useNextSequence();
  const prev = usePrevSequence();

  const globalMedia = useGlobalMediaStore();
  const globalPlayer = globalMedia?.player;

  const goNext = () => {
    clearBlocker();
    if (globalPlayer) {
      try {
        globalPlayer?.pause();
      } catch (error) {
        // Silently ignore player operation errors
      }
    }
    setTimeout(() => next(), 100);
  };

  const goPrev = () => {
    clearBlocker();
    if (globalPlayer) {
      try {
        globalPlayer?.pause();
      } catch (error) {
        // Silently ignore player operation errors
      }
    }
    setTimeout(() => prev(), 100);
  };

  if (!skip || params.get('removeAllButtons') === 'true') {
    return null;
  }

  const allSequences = items.filter(
    (item): item is Sequence => item.kind == 'sequence'
  );

  return (
    <SkipSection>
      <div className="devices">
        <LightButton className="">
          <DesktopIcon />
        </LightButton>
      </div>
      <div className="navBtns">
        <LightButton
          onClick={goPrev}
          className="arrow arrow-left"
          disabled={allSequences[0]?.position == activeItemPosition}
        >
          <ArrowIcon />
        </LightButton>
        <LightButton
          onClick={goNext}
          className="arrow arrow-right"
          disabled={
            allSequences[allSequences.length - 1]?.position ==
            activeItemPosition
          }
        >
          <ArrowIcon />
        </LightButton>
      </div>
      <div className="preview-mode">{t`PREVIEW MODE`}</div>
    </SkipSection>
  );
};
